import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Form, Input, Button, Checkbox, Typography, Alert} from "antd";
import api from "../api";

const {Title} = Typography;

const PENDING_MFA_TOKEN_KEY = "pending_mfa_token";
const PENDING_MFA_REMEMBER_KEY = "pending_mfa_remember";

const Login: React.FC = () => {
    const [form] = Form.useForm();
    const [mfaForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mfaLoading, setMfaLoading] = useState(false);
    const [requiresMfa, setRequiresMfa] = useState(false);
    const [mfaToken, setMfaToken] = useState<string | null>(null);
    const [rememberChoice, setRememberChoice] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const access = localStorage.getItem("access") || sessionStorage.getItem("access");
        const user = JSON.parse(
            localStorage.getItem("user") || sessionStorage.getItem("user") || "{}"
        );

        if (access && user.account_type) {
            if (!user.mfa_exempt && !user.mfa_enabled) {
                navigate("/setup-mfa", {replace: true});
                return;
            }

            if (user.account_type === "ccdaia") {
                navigate("/cc/registrations", {replace: true});
            } else if (user.account_type === "dolphin") {
                navigate("/dolphin/rates", {replace: true});
            } else if (user.account_type === "agent") {
                navigate("/agent/settlements", {replace: true});
            }
            return;
        }

        const pendingMfaToken = sessionStorage.getItem(PENDING_MFA_TOKEN_KEY);
        const pendingRemember = sessionStorage.getItem(PENDING_MFA_REMEMBER_KEY);

        if (pendingMfaToken) {
            setRequiresMfa(true);
            setMfaToken(pendingMfaToken);
            setRememberChoice(pendingRemember === "true");
        }
    }, [navigate]);

    const clearPendingMfa = () => {
        sessionStorage.removeItem(PENDING_MFA_TOKEN_KEY);
        sessionStorage.removeItem(PENDING_MFA_REMEMBER_KEY);
    };

    const persistAuth = async (access: string, refresh: string, remember: boolean) => {
        clearPendingMfa();

        if (remember) {
            localStorage.setItem("access", access);
            localStorage.setItem("refresh", refresh);

            sessionStorage.removeItem("access");
            sessionStorage.removeItem("refresh");
            sessionStorage.removeItem("user");
        } else {
            sessionStorage.setItem("access", access);
            sessionStorage.setItem("refresh", refresh);

            localStorage.removeItem("access");
            localStorage.removeItem("refresh");
            localStorage.removeItem("user");
        }

        const userRes = await api.get("/auth/users/me/");
        const user = userRes.data;

        if (remember) {
            localStorage.setItem("user", JSON.stringify(user));
        } else {
            sessionStorage.setItem("user", JSON.stringify(user));
        }

        if (!user.mfa_exempt && !user.mfa_enabled) {
            navigate("/setup-mfa", {replace: true});
            return;
        }

        if (user.account_type === "ccdaia") {
            navigate("/cc/registrations", {replace: true});
        } else if (user.account_type === "dolphin") {
            navigate("/dolphin/rates", {replace: true});
        } else if (user.account_type === "agent") {
            navigate("/agent/settlements", {replace: true});
        } else {
            navigate("/", {replace: true});
        }
    };

    const onFinish = async ({
                                username,
                                password,
                                remember,
                            }: {
        username: string;
        password: string;
        remember: boolean;
    }) => {
        setLoading(true);
        try {
            const res = await api.post("/auth/jwt/create/", {username, password});
            if (res.data.requires_mfa) {
                setRequiresMfa(true);
                setMfaToken(res.data.mfa_token);
                setRememberChoice(remember);

                sessionStorage.setItem(PENDING_MFA_TOKEN_KEY, res.data.mfa_token);
                sessionStorage.setItem(PENDING_MFA_REMEMBER_KEY, String(remember));

                return;
            }

            const {access, refresh} = res.data;
            await persistAuth(access, refresh, remember);
        } catch (err: any) {
            const msg =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Login failed. Try again.";
            form.setFields([{name: "password", errors: [msg]}]);
        } finally {
            setLoading(false);
        }
    };

    const onMfaFinish = async ({code}: { code: string }) => {
        if (!mfaToken) return;

        setMfaLoading(true);
        try {
            const res = await api.post("/users/mfa/verify/", {
                mfa_token: mfaToken,
                code,
            });

            const {access, refresh} = res.data;
            await persistAuth(access, refresh, rememberChoice);
        } catch (err: any) {
            const msg =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Invalid MFA code.";
            mfaForm.setFields([{name: "code", errors: [msg]}]);
        } finally {
            setMfaLoading(false);
        }
    };

    return (
        <div
            style={{
                height: "100vh",
                background: "#f0f2f5",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <div
                style={{
                    width: 360,
                    background: "#fff",
                    padding: "40px 30px",
                    borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Title level={3} style={{textAlign: "center", marginBottom: 32}}>
                    DAIA
                </Title>

                {!requiresMfa ? (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{remember: true}}
                    >
                        <Form.Item
                            name="username"
                            label="Username"
                            rules={[{required: true, message: "Please enter your username"}]}
                        >
                            <Input placeholder="Username"/>
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[{required: true, message: "Please enter your password"}]}
                        >
                            <Input.Password placeholder="Password"/>
                        </Form.Item>

                        <Form.Item name="remember" valuePropName="checked">
                            <Checkbox>Remember me</Checkbox>
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                block
                                style={{marginTop: 10}}
                            >
                                Login
                            </Button>
                        </Form.Item>
                    </Form>
                ) : (
                    <>
                        <Alert
                            message="Multi-factor authentication required"
                            description="Enter the 6-digit code from your authenticator app or a backup code."
                            type="info"
                            showIcon
                            style={{marginBottom: 20}}
                        />

                        <Form form={mfaForm} layout="vertical" onFinish={onMfaFinish}>
                            <Form.Item
                                name="code"
                                label="Authentication Code"
                                rules={[{required: true, message: "Please enter your code"}]}
                            >
                                <Input
                                    placeholder="123456 or backup code"
                                    maxLength={12}
                                    autoFocus
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={mfaLoading} block>
                                    Verify Code
                                </Button>
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    block
                                    onClick={() => {
                                        clearPendingMfa();
                                        setRequiresMfa(false);
                                        setMfaToken(null);
                                        mfaForm.resetFields();
                                    }}
                                >
                                    Back
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </div>
        </div>
    );
};

export default Login;